/**
 * AdminHealthMonitorPage — Ampel-Übersicht + Verlauf des Produktions-Health-Monitors.
 * Route: /admin/health-monitor (admin only)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HeartPulse, Check, AlertTriangle, ArrowLeft, RefreshCw, PlayCircle, Globe } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
};

export default function AdminHealthMonitorPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/health-monitor");
            setData(r.data);
        } catch (_e) { setData(null); }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
        if (user) {
            load();
            const iv = setInterval(load, 60000);
            return () => clearInterval(iv);
        }
    }, [user, navigate, load]);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post("/admin/health-monitor/run");
            toast[r.data.ok ? "success" : "error"](
                r.data.ok ? "Alle Checks bestanden ✅" : `Fehlgeschlagen: ${r.data.failed.join(", ")}`);
            await load();
        } catch (_e) { toast.error("Check konnte nicht ausgeführt werden"); }
        setRunning(false);
    };

    if (!user) return <div className="p-8">Lade…</div>;
    const last = data?.last || data?.history?.[0];
    const checks = last?.checks || [];

    return (
        <div className="min-h-screen bg-gray-50" data-testid="health-monitor-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/admin" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-admin">
                        <ArrowLeft className="w-3 h-3" /> Admin-Dashboard
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <HeartPulse className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <div className="text-xs uppercase tracking-wider text-gray-400">Monitoring · Produktion</div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Health-Monitor</h1>
                                <p className="text-sm text-gray-300 mt-1 flex items-center gap-1">
                                    <Globe className="w-3.5 h-3.5" /> {data?.base_url || "twowheels.info"}
                                    <span className={`ml-2 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${data?.enabled ? "bg-green-500/20 text-green-300" : "bg-amber-500/20 text-amber-300"}`} data-testid="monitor-enabled-badge">
                                        {data?.enabled ? "Auto-Überwachung aktiv" : "Auto-Überwachung nur in Produktion"}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={runNow} disabled={running} className="bg-[#FFCC00] text-[#111827] hover:bg-yellow-400 rounded-sm font-bold" data-testid="run-check-btn">
                                <PlayCircle className={`w-4 h-4 mr-1 ${running ? "animate-spin" : ""}`} /> Jetzt prüfen
                            </Button>
                            <Button onClick={load} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 rounded-sm" data-testid="refresh-btn">
                                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Neu laden
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {last ? (
                    <div className={`border rounded-sm p-5 flex items-center gap-4 ${last.ok ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`} data-testid="monitor-ampel">
                        <span className={`w-5 h-5 rounded-full shrink-0 ${last.ok ? "bg-green-500" : "bg-red-500 animate-pulse"}`} />
                        <div>
                            <div className={`font-display font-black text-2xl ${last.ok ? "text-green-900" : "text-red-900"}`}>
                                {last.ok ? "Alles OK" : `${last.failed?.length || 0} Check(s) fehlgeschlagen`}
                            </div>
                            <div className="text-xs text-gray-600">Letzter Check: {fmtDate(last.ran_at)}{last.alert_sent_at && <span className="ml-2 text-amber-700 font-bold">· 📧 Alert gesendet: {fmtDate(last.alert_sent_at)}</span>}</div>
                        </div>
                    </div>
                ) : (
                    !loading && (
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 text-sm text-amber-900" data-testid="monitor-no-data">
                            Noch kein Check gelaufen — klicke auf <strong>„Jetzt prüfen"</strong> für einen Sofort-Check.
                        </div>
                    )
                )}

                {checks.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="monitor-checks">
                        {checks.map((c) => (
                            <div key={c.name} className={`border rounded-sm p-3 ${c.ok ? "bg-white border-gray-200" : "bg-red-50 border-red-300"}`} data-testid={`check-${c.name.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}>
                                <div className="flex items-center gap-2">
                                    {c.ok
                                        ? <Check className="w-4 h-4 text-green-600 shrink-0" />
                                        : <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />}
                                    <span className="font-bold text-[13px]">{c.name}</span>
                                </div>
                                <div className={`text-[11px] font-mono-num mt-1 truncate ${c.ok ? "text-gray-500" : "text-red-700"}`} title={c.detail}>{c.detail || "—"}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="monitor-history">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                        Verlauf (letzte {data?.history?.length || 0} Checks)
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Zeitpunkt</th>
                                <th className="text-left px-3 py-2">Auslöser</th>
                                <th className="text-left px-3 py-2">Fehlgeschlagene Checks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.history?.map((h, i) => (
                                <tr key={h.ran_at + i} className={`border-t border-gray-100 ${!h.ok ? "bg-red-50/40" : ""}`} data-testid={`history-row-${i}`}>
                                    <td className="px-3 py-2">
                                        {h.ok
                                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-900"><Check className="w-3 h-3" /> OK</span>
                                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3" /> Fehler</span>}
                                    </td>
                                    <td className="px-3 py-2 text-[11px] font-mono-num text-gray-600 whitespace-nowrap">{fmtDate(h.ran_at)}</td>
                                    <td className="px-3 py-2 text-[11px] text-gray-600">{h.source === "manual" ? "Manuell" : "Automatisch"}</td>
                                    <td className="px-3 py-2 text-[11px] text-red-700">{h.failed?.length ? h.failed.join(", ") : <span className="text-gray-300">—</span>}</td>
                                </tr>
                            ))}
                            {(!data?.history?.length && !loading) && (
                                <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-500" data-testid="history-empty">Noch kein Verlauf vorhanden</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-3 text-xs text-blue-900">
                    <strong>So funktioniert's:</strong> In Produktion läuft der Check automatisch <strong>stündlich</strong> gegen {data?.base_url || "twowheels.info"}.
                    Bei einem Ausfall wird sofort eine <strong>E-Mail</strong> (info@twowheels.info) und eine <strong>WhatsApp</strong> versendet (Cooldown 6h),
                    plus eine Entwarnung, sobald wieder alles grün ist. „Jetzt prüfen" führt einen Sofort-Check ohne Alert-Versand aus.
                </div>
            </div>
        </div>
    );
}
